import { FC, ChangeEvent } from 'react';
import { Box, MenuItem, Select as MuiSelect, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { getMuiSize } from '../helpers';
import Text from '@/components/text';
import { getNewServiceActionOptions } from '@/lib/models/policy-model/public-services';
import { ServiceAction } from './helpers';

interface Props {
  value: ServiceAction | '';
  onChange: (value: ServiceAction) => void;
  selectedAction?: ServiceAction;
}

const useStyles = createUseStyles((theme: Theme) => ({
  newServicesActionSelect: {
    backgroundColor: theme.palette.common.white,
    '& fieldset, & .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
    },
    '&.empty-state': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.main,
        filter: 'brightness(0.9)',
      },
      '& .MuiSelect-icon': {
        color: theme.palette.common.white,
      },
    },
    '&:not(.empty-state)': {
      '& .MuiOutlinedInput-input': {
        backgroundColor: theme.palette.common.white,
      },
    },
  },
  menuItem: {
    width: 300,
    whiteSpace: 'wrap',
  },
}));

const NewServiceActionSelect: FC<Props> = ({ value, onChange, selectedAction }) => {
  const styles = useStyles();
  const options = getNewServiceActionOptions();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value as ServiceAction);
  };

  return (
    <MuiSelect
      value={value}
      onChange={handleChange}
      displayEmpty
      fullWidth
      size={getMuiSize('x-small')}
      className={`${styles.newServicesActionSelect} ${!selectedAction ? 'empty-state' : `select-${selectedAction}`}`}
      renderValue={(selected) => {
        const isEmpty = !selected;
        const label = isEmpty
          ? 'Acknowledge'
          : options.find(({ value }) => value === selected)?.label || '';
        return (
          <Text size="medium" color={isEmpty ? 'white' : 'black'}>
            {label}
          </Text>
        );
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      }}
    >
      {options.map(({ value, label, description }) => {
        const hasDescription = !!description;
        return (
          <MenuItem key={value} value={value} className={styles.menuItem}>
            <Stack width="100%">
              <Box>
                <Text color="black" dotdot>
                  {label}
                </Text>
              </Box>
              {hasDescription && (
                <Box mt={0.5}>
                  <Text size="small" color="grey">
                    {description}
                  </Text>
                </Box>
              )}
            </Stack>
          </MenuItem>
        );
      })}
    </MuiSelect>
  );
};

export default NewServiceActionSelect;
